import { ReportData, SalesSource } from '../types/reportTypes';
import { combineReports } from '../utils/reportAggregationUtils';
import { octoberData } from './octoberData';
import { novemberData } from './novemberData';
import { decemberData } from './decemberData';

// All monthly reports to aggregate for Q4 2025
const allMonthlyReports = [
  octoberData,
  novemberData,
  decemberData
];

// Combine all three months into Q4 data
const combinedData = combineReports(allMonthlyReports, 'Q4 2025 Standard Report');

// Export the Q4 data with proper period information
export const q4Data: ReportData = {
  ...combinedData,
  period: {
    month: 10, // First month of Q4 (October)
    year: 2025,
    quarter: 4,
    startMonth: 10,
    endMonth: 12
  }
};

// Additional data for trend analysis in the quarterly report
export interface Q4TrendData {
  monthlyTrends: {
    month: string;
    totalSales: number;
    grossProfit: number;
    netProfit: number;
    adSpend: number;
    roi: number;
    grossMarginPct: number;
    netMarginPct: number;
    salesCount: number;
    avgSaleValue: number;
  }[];
  sourcePerformance: SourcePerformance[];
  leadSourceTrends: {
    source: string;
    october: { sales: number; count: number; roi: number };
    november: { sales: number; count: number; roi: number };
    december: { sales: number; count: number; roi: number };
    q4Total: { sales: number; count: number; roi: number };
  }[];
}

// Extended interface for source performance
interface SourcePerformance extends SalesSource {
  adSpend: number;
  roi: number;
  costPerSale: number;
}

// Generate trend data for Q4 analysis
export const q4TrendData: Q4TrendData = {
  monthlyTrends: [
    {
      month: "October",
      totalSales: octoberData.kpis.totalSales,
      grossProfit: octoberData.kpis.grossProfit,
      netProfit: octoberData.kpis.netProfit,
      adSpend: octoberData.kpis.adSpend,
      roi: octoberData.kpis.adSpend > 0 ? octoberData.kpis.grossProfit / octoberData.kpis.adSpend : 0,
      grossMarginPct: octoberData.kpis.grossProfitPercent,
      netMarginPct: octoberData.kpis.netProfitPercent,
      salesCount: octoberData.jobData.length,
      avgSaleValue: octoberData.kpis.totalSales / octoberData.jobData.length
    },
    {
      month: "November",
      totalSales: novemberData.kpis.totalSales,
      grossProfit: novemberData.kpis.grossProfit,
      netProfit: novemberData.kpis.netProfit,
      adSpend: novemberData.kpis.adSpend,
      roi: novemberData.kpis.adSpend > 0 ? novemberData.kpis.grossProfit / novemberData.kpis.adSpend : 0,
      grossMarginPct: novemberData.kpis.grossProfitPercent,
      netMarginPct: novemberData.kpis.netProfitPercent,
      salesCount: novemberData.jobData.length,
      avgSaleValue: novemberData.kpis.totalSales / novemberData.jobData.length
    },
    {
      month: "December",
      totalSales: decemberData.kpis.totalSales,
      grossProfit: decemberData.kpis.grossProfit,
      netProfit: decemberData.kpis.netProfit,
      adSpend: decemberData.kpis.adSpend,
      roi: decemberData.kpis.adSpend > 0 ? decemberData.kpis.grossProfit / decemberData.kpis.adSpend : 0,
      grossMarginPct: decemberData.kpis.grossProfitPercent,
      netMarginPct: decemberData.kpis.netProfitPercent,
      salesCount: decemberData.jobData.length,
      avgSaleValue: decemberData.kpis.totalSales / decemberData.jobData.length
    }
  ],

  // Calculate source performance with ROI
  sourcePerformance: (() => {
    const salesBySource = combinedData.salesBySource;
    const adSpendBySource = combinedData.adSpendBySource;

    return salesBySource.map(source => {
      const adSpendItem = adSpendBySource.find(ad =>
        ad.source.toLowerCase().includes(source.source.toLowerCase()) ||
        source.source.toLowerCase().includes(ad.source.toLowerCase())
      );

      const adSpend = adSpendItem?.spend || 0;
      const roi = adSpend > 0 ? source.grossProfit / adSpend : 0;
      const costPerSale = source.count > 0 ? adSpend / source.count : 0;

      return {
        ...source,
        adSpend,
        roi,
        costPerSale
      };
    }).sort((a, b) => b.roi - a.roi);
  })(),

  // Generate lead source trends
  leadSourceTrends: (() => {
    // Get unique sources across all months
    const allSources = new Set<string>();
    [octoberData.salesBySource, novemberData.salesBySource, decemberData.salesBySource].forEach(monthSources => {
      monthSources.forEach(source => allSources.add(source.source));
    });

    return Array.from(allSources).map(sourceName => {
      // Find data for each month
      const octoberSource = octoberData.salesBySource.find(s => s.source === sourceName) ||
                         { totalSales: 0, count: 0, grossProfit: 0 };
      const novemberSource = novemberData.salesBySource.find(s => s.source === sourceName) ||
                           { totalSales: 0, count: 0, grossProfit: 0 };
      const decemberSource = decemberData.salesBySource.find(s => s.source === sourceName) ||
                              { totalSales: 0, count: 0, grossProfit: 0 };

      // Find ad spend for ROI calculation
      const octoberAdSpend = octoberData.adSpendBySource.find(ad =>
        ad.source.toLowerCase().includes(sourceName.toLowerCase()) ||
        sourceName.toLowerCase().includes(ad.source.toLowerCase())
      )?.spend || 0;

      const novemberAdSpend = novemberData.adSpendBySource.find(ad =>
        ad.source.toLowerCase().includes(sourceName.toLowerCase()) ||
        sourceName.toLowerCase().includes(ad.source.toLowerCase())
      )?.spend || 0;

      const decemberAdSpend = decemberData.adSpendBySource.find(ad =>
        ad.source.toLowerCase().includes(sourceName.toLowerCase()) ||
        sourceName.toLowerCase().includes(ad.source.toLowerCase())
      )?.spend || 0;

      const totalAdSpend = octoberAdSpend + novemberAdSpend + decemberAdSpend;
      const totalGrossProfit = octoberSource.grossProfit + novemberSource.grossProfit + decemberSource.grossProfit;

      return {
        source: sourceName,
        october: {
          sales: octoberSource.totalSales,
          count: octoberSource.count,
          roi: octoberAdSpend > 0 ? octoberSource.grossProfit / octoberAdSpend : 0
        },
        november: {
          sales: novemberSource.totalSales,
          count: novemberSource.count,
          roi: novemberAdSpend > 0 ? novemberSource.grossProfit / novemberAdSpend : 0
        },
        december: {
          sales: decemberSource.totalSales,
          count: decemberSource.count,
          roi: decemberAdSpend > 0 ? decemberSource.grossProfit / decemberAdSpend : 0
        },
        q4Total: {
          sales: octoberSource.totalSales + novemberSource.totalSales + decemberSource.totalSales,
          count: octoberSource.count + novemberSource.count + decemberSource.count,
          roi: totalAdSpend > 0 ? totalGrossProfit / totalAdSpend : 0
        }
      };
    }).sort((a, b) => b.q4Total.sales - a.q4Total.sales);
  })()
};
